import React, { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import * as momentRef from 'moment';
import moment from 'moment-timezone';

import { DATE_FORMAT, TIME_FORMAT } from 'appConstants';
import { DatePicker, TimePicker } from 'components/Layout/Form';
import { TRewardListPage } from 'containers/Reward/i18n/TModules';

interface DateTimePickerProps {
  timezone?: string;
  value: momentRef.Moment | null;
  // Note: at runtime onChange is sometimes invoked with an ISO string (from
  // dateChanged) or null (from timeChanged with no value). Consumers rely on
  // the original Moment-narrowed signature, so we keep that contract here.
  onChange: (date: momentRef.Moment | null) => void;
  showDate?: boolean;
  showTime?: boolean;
  isDisableFutureDate?: boolean;
  isDisablePastDate?: boolean;
  disabledDate?: (current: momentRef.Moment) => boolean;
  minDateTime?: momentRef.Moment | string | null;
  datePickerId?: string;
  timePickerId?: string;
  getCalendarContainer?: (triggerNode: Element) => HTMLElement;
  getPopupContainer?: (triggerNode: Element) => HTMLElement;
  disabled?: boolean;
  style?: CSSProperties;
}

const disabledPastDate = (current: momentRef.Moment) => current && current < moment().endOf('day');

const disabledFutureDate = (current: momentRef.Moment) =>
  current && current > moment().endOf('day');

const range = (start: number, end: number) => {
  const result: number[] = [];
  for (let i = start; i < end; i++) result.push(i);
  return result;
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  timezone = 'UTC',
  value,
  onChange,
  showDate,
  showTime,
  isDisableFutureDate,
  isDisablePastDate,
  disabledDate,
  minDateTime,
  datePickerId,
  timePickerId,
  getCalendarContainer,
  getPopupContainer,
  disabled,
  style = {}
}) => {
  const { t } = useTranslation();

  const enforceMinDateTime = (candidate: momentRef.Moment | null) => {
    if (!candidate || !minDateTime) return candidate;
    const minMoment = moment.tz(minDateTime, timezone);
    const candidateMoment = moment.tz(candidate, timezone);
    if (candidateMoment.isSame(minMoment, 'day') && candidateMoment.isBefore(minMoment)) {
      return minMoment;
    }
    return candidate;
  };

  const dateChanged = (incoming: any, valueString: string) => {
    let newValue: any = null;
    if (incoming) {
      const time = value ? moment.tz(value, timezone).format('HH:mm') : '00:00';
      if (valueString) {
        let nextMoment = moment.tz(valueString + ' ' + time, timezone);
        nextMoment = enforceMinDateTime(nextMoment) as momentRef.Moment;
        newValue = nextMoment ? nextMoment.toISOString() : '';
      } else {
        newValue = '';
      }
    }
    onChange?.(newValue);
  };

  const timeChanged = (incoming: any, valueString: string) => {
    if (incoming && valueString) {
      const v = moment.tz(incoming, timezone);
      const time = moment(incoming);
      v.set({ hour: time.get('hour'), minute: time.get('minute'), second: time.get('second') });

      if (value) {
        const m = moment.tz(value, timezone);
        v.set({ date: m.get('date'), month: m.get('month'), year: m.get('year') });
      }
      onChange?.(enforceMinDateTime(v));
    } else {
      onChange?.(null);
    }
  };

  const getDisabledTime = (selectedDateTime: momentRef.Moment | null) => {
    if (!minDateTime || !selectedDateTime) return {};
    const minMoment = moment.tz(minDateTime, timezone);
    const selectedMoment = moment.tz(selectedDateTime, timezone);
    if (!selectedMoment.isSame(minMoment, 'day')) return {};
    const minHour = minMoment.hour();
    const minMinute = minMoment.minute();
    const minSecond = minMoment.second();
    return {
      disabledHours: () => range(0, minHour),
      disabledMinutes: (selectedHour: number) =>
        selectedHour === minHour ? range(0, minMinute) : [],
      disabledSeconds: (selectedHour: number, selectedMinute: number) =>
        selectedHour === minHour && selectedMinute === minMinute ? range(0, minSecond) : []
    };
  };

  const dateTime = value ? moment.tz(value, timezone) : null;
  const disableDate =
    disabledDate ||
    (isDisableFutureDate ? disabledFutureDate : isDisablePastDate ? disabledPastDate : undefined);

  return (
    <div style={{ display: 'flex', flexFlow: 'wrap', gap: '0.5rem' }}>
      {showDate && (
        <DatePicker
          className="datePicker"
          disabledDate={disableDate}
          style={{ flex: '1 0 130px', marginRight: 10, ...style }}
          value={dateTime ? moment(dateTime.format('YYYY-MM-DD'), 'YYYY-MM-DD') : undefined}
          onChange={(val: any, valString: any) => dateChanged(val, valString)}
          format={DATE_FORMAT}
          getPopupContainer={getCalendarContainer}
          disabled={disabled}
          placeholder={`${t(`${TRewardListPage}.SelectDate`)} `}
          data-testid={datePickerId}
        />
      )}

      {showTime && (
        <TimePicker
          className="timePicker"
          style={{ flex: '1 0 130px', marginRight: 10, ...style }}
          value={
            dateTime
              ? moment(
                  moment(new Date()).format('YYYY-MM-DD') + ' ' + dateTime.format('HH:mm'),
                  'YYYY-MM-DD HH:mm'
                )
              : undefined
          }
          onChange={(val: any, valString: any) => timeChanged(val, valString)}
          format={TIME_FORMAT}
          placeholder={`${t(`${TRewardListPage}.SelectTime`)}`}
          getPopupContainer={getPopupContainer}
          {...getDisabledTime(dateTime)}
          disabled={disabled}
          data-testid={timePickerId}
        />
      )}
    </div>
  );
};

export default DateTimePicker;
